// =============================================================================
// OrderDetailSheet.tsx
// Bottom sheet shown when the user taps an order on the mobile dashboard or
// reports tab. Header → status change row → meta → line items → audit log →
// 2×2 action grid (Edit, Stock, Delete). Module-level memoized component
// (extracted in Stage 1 to fix the FilterSheet/OrderDetailSheet remount bug).
// =============================================================================

import React, { useState } from 'react';

import { ConfirmModal } from '../../ui';

import { updateOrderStatus, deleteOrder, inventoryAdd, markOrderStocked, getOrderAuditLog } from '../../../lib/api';
import type { OrderAuditEntry } from '../../../lib/api';
import { formatCurrency } from '../../../lib/utils';

import { Order, OrderStatus } from '../../../lib/types';

import { S, ALL_STATUSES, STATUS_COLORS, STATUS_TONE, closeSheetAnim } from '../mobileStyles';

// ── TYPES ────────────────────────────────────────────────────────────

interface ConfirmState {
  message: string;
  title?: string;
  confirmLabel: string;
  destructive: boolean;
  onConfirm: () => void;
}

interface OrderDetailSheetProps {
  selectedOrder: Order | null;
  setSelectedOrder: (o: Order | null) => void;
  setEditingOrder: (o: Order) => void;
  setShowAddOrder: (v: boolean) => void;
  closingSheet: string | null;
  setClosingSheet: (v: string | null) => void;
  currency: string;
  locale: string;
  onOrderAdded: () => void;
  onInventoryChanged: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────

function OrderDetailSheetImpl({
  selectedOrder, setSelectedOrder, setEditingOrder, setShowAddOrder,
  closingSheet, setClosingSheet, currency, locale,
  onOrderAdded, onInventoryChanged, showToast,
}: OrderDetailSheetProps) {
  if (!selectedOrder) return null;
  const o = selectedOrder;
  const items = o.order_items ?? [];
  const canStock = o.status === 'Delivered' && items.length > 0 && !o.stocked_at;

  // ── state ──
  const [auditLog,     setAuditLog]     = useState<OrderAuditEntry[]>([]);
  const [showHistory,  setShowHistory]  = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [statusBusy,   setStatusBusy]   = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  // ── handlers ──

  const loadHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    setAuditLoading(true);
    const res = await getOrderAuditLog(o.id);
    setAuditLog(res.ok && res.data ? res.data : []);
    setShowHistory(true);
    setAuditLoading(false);
  };

  const performStatusChange = async (newStatus: OrderStatus) => {
    setStatusBusy(true);
    await updateOrderStatus(o.id, newStatus, o.updated_at);
    onOrderAdded();
    setStatusBusy(false);
    setSelectedOrder(null);
    showToast(`Status changed to ${newStatus}`, 'success');
  };

  const handleStatusChange = (newStatus: OrderStatus) => {
    if (newStatus === o.status || statusBusy) return;
    const currentIdx = ALL_STATUSES.indexOf(o.status as OrderStatus);
    const newIdx = ALL_STATUSES.indexOf(newStatus);
    // Pick the appropriate confirmation prompt for this transition.
    if (o.status === 'Closed') {
      setConfirmState({
        title: 'Reopen order?',
        message: 'This order was completed. Reopen it?',
        confirmLabel: 'Reopen',
        destructive: false,
        onConfirm: () => performStatusChange(newStatus),
      });
      return;
    }
    if (newIdx < currentIdx) {
      setConfirmState({
        title: 'Move status back?',
        message: `Move status back from ${o.status} to ${newStatus}?`,
        confirmLabel: 'Move back',
        destructive: false,
        onConfirm: () => performStatusChange(newStatus),
      });
      return;
    }
    if (newStatus === 'Closed') {
      setConfirmState({
        title: 'Complete order?',
        message: 'Mark this order as complete?',
        confirmLabel: 'Complete',
        destructive: false,
        onConfirm: () => performStatusChange(newStatus),
      });
      return;
    }
    void performStatusChange(newStatus);
  };

  const handleDelete = () => {
    setConfirmState({
      title: 'Delete order?',
      message: 'Delete this order? It can be restored later.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        await deleteOrder(o.id, o.updated_at);
        onOrderAdded();
        setSelectedOrder(null);
        showToast('Order deleted', 'success');
      },
    });
  };

  const handleEdit = () => {
    setEditingOrder(o);
    setShowAddOrder(true);
    setSelectedOrder(null);
  };

  const handleStock = async () => {
    for (const item of items) {
      await inventoryAdd({ item_name: item.item_name, sku: item.code, size: item.size, quantity: item.quantity || 1, target_department_id: o.department_id });
    }
    await markOrderStocked(o.id);
    onInventoryChanged(); onOrderAdded();
    showToast('Added to inventory', 'success');
    setSelectedOrder(null);
  };

  // ── render ──

  const headerTone = STATUS_TONE[o.status || ''] || 'gray';

  return (
    <>
      <div style={S.sheet} className={closingSheet === 'order' ? 'm-sheet-closing' : 'm-sheet-backdrop'} onClick={() => closeSheetAnim(setClosingSheet, setSelectedOrder, 'order')}>
        <div style={S.sheetContent} className="m-sheet-content" onClick={e => e.stopPropagation()}>
          <div style={S.handle as any} />

          {/* Header */}
          <div style={{ padding: '10px 20px 8px', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 600 }}>{items[0]?.item_name || 'Order'}</div>
              <div style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text3)', marginTop: 3 }}>{o.vendor || '—'} · {o.order_number || '—'}</div>
            </div>
            <span className={`pill-u ${headerTone}`}>{o.status}</span>
          </div>

          {/* Status change pills */}
          <div style={{ padding: '10px 20px', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text3)', marginBottom: 6 }}>Change status</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {ALL_STATUSES.map(s => {
                const isActive = o.status === s;
                const sColor = STATUS_COLORS[s];
                return (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    disabled={statusBusy}
                    style={{
                      padding: '5px 12px', borderRadius: 14, fontSize: 12, fontWeight: isActive ? 600 : 500,
                      border: isActive ? `1.5px solid ${sColor.text}` : '1.5px solid transparent',
                      background: isActive ? sColor.bg : 'var(--surface2)',
                      color: isActive ? sColor.text : 'var(--text3)',
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease',
                      opacity: statusBusy ? 0.5 : 1,
                    }}
                  >{s}</button>
                );
              })}
            </div>
          </div>

          {/* Meta */}
          <div style={{ padding: '10px 20px', display: 'flex', flexWrap: 'wrap', gap: 16, borderBottom: '0.5px solid var(--border)' }}>
            {o.staff_name      && <div style={{ flex: 1 }}><div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginBottom: 2 }}>Ordered for</div><div style={{ fontSize: 'var(--m-text-meta)', fontWeight: 500 }}>{o.staff_name}</div></div>}
            {o.category        && <div style={{ flex: 1 }}><div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginBottom: 2 }}>Category</div><div style={{ fontSize: 'var(--m-text-meta)', fontWeight: 500 }}>{o.category}</div></div>}
            {o.department_name && <div style={{ flex: 1 }}><div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginBottom: 2 }}>Dept</div><div style={{ fontSize: 'var(--m-text-meta)', fontWeight: 500 }}>{o.department_name}</div></div>}
            {o.document_type && o.document_type !== 'order_confirmation' && <div style={{ flex: 1 }}><div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginBottom: 2 }}>Doc type</div><div style={{ fontSize: 'var(--m-text-meta)', fontWeight: 500, textTransform: 'capitalize' }}>{o.document_type.replace(/_/g, ' ')}</div></div>}
            {o.expected_delivery && <div style={{ flex: 1 }}><div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginBottom: 2 }}>Exp. delivery</div><div style={{ fontSize: 'var(--m-text-meta)', fontWeight: 500 }}>{o.expected_delivery}</div></div>}
          </div>

          {/* Line items */}
          <div style={{ padding: '10px 20px', borderBottom: '0.5px solid var(--border)' }}>
            {items.map((item, i) => (
              <div key={item.id || i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < items.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--m-text-meta)' }}>{item.item_name}</div>
                  {(item.code || item.size) && <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>{item.code}{item.code && item.size ? ' · ' : ''}{item.size}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 'var(--m-text-meta)', fontWeight: 600 }}>{item.unit_price ? formatCurrency(item.quantity * item.unit_price, currency) : ''}</div>
                  <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>x{item.quantity}{item.unit_price ? ` · ${currency}${item.unit_price.toFixed(2)} ea` : ''}</div>
                </div>
              </div>
            ))}
            {o.total_cost != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 4, borderTop: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 'var(--m-text-meta)', fontWeight: 600 }}>Total</span>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{formatCurrency(o.total_cost, currency)}</span>
              </div>
            )}
          </div>

          {/* Audit history */}
          <div style={{ padding: '8px 20px', borderBottom: '0.5px solid var(--border)' }}>
            <button
              onClick={loadHistory}
              style={{ fontSize: 'var(--m-text-label)', color: 'var(--accent)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
            >
              {showHistory ? 'Hide history ▴' : 'History ▾'}
            </button>
            {auditLoading && <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginTop: 4 }}>Loading…</div>}
            {showHistory && !auditLoading && (
              <div style={{ marginTop: 6 }}>
                {auditLog.length === 0 ? (
                  <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>No history recorded yet</div>
                ) : auditLog.map(entry => (
                  <div key={entry.id} style={{ fontSize: 'var(--m-text-label)', color: 'var(--text2)', padding: '3px 0' }}>
                    <span style={{ color: 'var(--text3)', fontSize: 'var(--m-text-small)' }}>
                      {new Date(entry.created_at).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}{' '}
                      {new Date(entry.created_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {' — '}
                    <strong>{entry.performed_by_name}</strong>
                    {entry.action === 'created'       && <> created this order</>}
                    {entry.action === 'status_change' && <> changed {entry.old_value} → {entry.new_value}</>}
                    {entry.action === 'field_update'  && <> updated {entry.field_name} to "{entry.new_value}"</>}
                    {entry.action === 'deleted'       && <> deleted this order</>}
                    {entry.action === 'restored'      && <> restored this order</>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ padding: '12px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, paddingBottom: 24 }}>
            <button style={S.actionBtn('var(--surface2)', 'var(--text)')} onClick={handleEdit}>
              <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, fill: 'none', stroke: 'var(--text2)', strokeWidth: 1.3 }}><path d="M9.5 3.5l3 3M3 10.5L10 3.5l3 3L6 13.5H3v-3z" /></svg>
              Edit
            </button>
            {canStock && (
              <button style={S.actionBtn('var(--teal-bg)', 'var(--accent)')} onClick={handleStock}>
                <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, fill: 'none', stroke: 'var(--accent)', strokeWidth: 1.3 }}><rect x="2" y="5" width="12" height="9" rx="2" /><path d="M5 5V3.5C5 2.7 5.7 2 6.5 2h3c.8 0 1.5.7 1.5 1.5V5" /><path d="M8 8v3M6.5 9.5h3" /></svg>
                Stock
              </button>
            )}
            <button style={S.actionBtn('var(--red-bg)', 'var(--red)')} onClick={handleDelete}>
              <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, fill: 'none', stroke: 'var(--red)', strokeWidth: 1.3 }}><path d="M3 4h10M6 4V3h4v1M5 4v8.5c0 .5.5 1 1 1h4c.5 0 1-.5 1-1V4" /></svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      {confirmState && (
        <ConfirmModal
          open
          variant="sheet"
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          destructive={confirmState.destructive}
          onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </>
  );
}

export const OrderDetailSheet = React.memo(OrderDetailSheetImpl);
